import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useUpdateVerifierPositions } from "../../../graphql/hooks/invoiceInterpretationTemplates/useUpdateVerifierPositions";
import { useFocusNext } from "../../../hooks/useFocusNext";
import { useParams } from "react-router-dom";
import BodySmall from "../../atoms/typography/BodySmall";
import { HorizontalSpacer } from "../../atoms/Spacers";
import styled from "styled-components";
import { theme } from "../../../theme";
import Divider from "../../atoms/Divider";
import { InfoIconSmall } from "../../atoms/icons/InfoIcons";
import type {
  InvoiceReaderVerifierPositionsModel,
  PossibleFieldPositionWithValue,
} from "../../../models/ClientInvoiceReaderTemplateModel";
import { Row } from "react-grid-system";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import { useTranslation } from "react-i18next";
import Alert from "../../molecules/Alert";
import type { TemplateTabHandle } from "../../../models/Templates";
import { stripTypename } from "../../../utils/graphql";
import { buildFieldOptions, positionKey } from "../../../utils/templateHelpers";
import { useGetTextAtPosition } from "../../../graphql/hooks/invoiceInterpretationTemplates/useGetTextAtPosition";
import type { CustomHighlight } from "../PdfTemplate";
import ToggleButton from "../../atoms/buttons/ToggleButton";
import { ViewOffIcon, ViewOnIcon } from "../../atoms/icons/ViewIcons";
import Body from "../../atoms/typography/Body";
import type { Position } from "../../../models/Position";
import AutocompleteSelect from "../../atoms/inputs/AutocompleteSelect";
import ToggleAllTextButton from "../../atoms/buttons/ToggleAllTextButton";
import ActiveFieldWrapper from "./ActiveFieldWrapper";

interface Props {
  possibleFields: Array<PossibleFieldPositionWithValue>;
  verifierPositions?: InvoiceReaderVerifierPositionsModel;
  onSaveAndContinue: () => void;
  highlightedFields: Array<CustomHighlight>;
  setHighlightedFields: (field: CustomHighlight[]) => void;
  onDirtyChange?: () => void;
  onNavigateToPage?: (page: number) => void;
  totalPages?: number;
  onSaveError?: (message: string) => void;
}

const FlexRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: nowrap;
`;

const FlexText = styled.div`
  flex: 1;
  min-width: 0;
`;

const VALIDATION_FIELD_BORDER = "rgba(156, 39, 176, 0.8)";
const VALIDATION_FIELD_BACKGROUND = "rgba(156, 39, 176, 0.15)";

const EditValidationFields = forwardRef<TemplateTabHandle, Props>(
  (
    {
      possibleFields,
      verifierPositions,
      onSaveAndContinue,
      highlightedFields,
      setHighlightedFields,
      onDirtyChange,
      onNavigateToPage,
      totalPages,
      onSaveError,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { id: clientIdentifier, templateId } = useParams();
    const { getTextAtPosition } = useGetTextAtPosition();
    const [verifierLabel, setVerifierLabel] =
      useState<PossibleFieldPositionWithValue | null>(null);
    const [extractionVerifier, setExtractionVerifier] =
      useState<PossibleFieldPositionWithValue | null>(null);
    const [dirty, setDirty] = useState(false);
    const dirtyRef = useRef(false);
    const navigateAfterSaveRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [activeFieldId, setActiveFieldId] = useState<"VERIFIER_LABEL" | "EXTRACTION_VERIFIER" | null>(null);
    const activeFieldIdRef = useRef<"VERIFIER_LABEL" | "EXTRACTION_VERIFIER" | null>(null);
    activeFieldIdRef.current = activeFieldId;
    const { formRef, lastFocusedInputRef, focusNextInput } = useFocusNext();
    const verifierLabelRef = useRef(verifierLabel);
    verifierLabelRef.current = verifierLabel;
    const extractionVerifierRef = useRef(extractionVerifier);
    extractionVerifierRef.current = extractionVerifier;
    const highlightedFieldsRef = useRef(highlightedFields);
    highlightedFieldsRef.current = highlightedFields;

    const markDirty = (isDirty: boolean) => {
      dirtyRef.current = isDirty;
      setDirty(isDirty);
      onDirtyChange?.();
    };

    const { updateVerifierPositions, loading } = useUpdateVerifierPositions({
      onError: (e) => {
        const msg = e?.message || t("errors.somethingWentWrong");
        setError(msg);
        onSaveError?.(msg);
      },
      onCompleted: (result) => {
        if (result.invoiceInterpretation?.updateVerifierPositions?.success) {
          if (navigateAfterSaveRef.current) { onSaveAndContinue(); }
        } else {
          setError(t("errors.somethingWentWrong"));
        }
      },
    });

    const checkIfFieldHighlighted = (fieldId: string) => {
      return highlightedFields.some((highlight) => highlight.label === fieldId);
    };

    const occurrencesOf = (
      fieldId: string,
      field: PossibleFieldPositionWithValue | null | undefined,
    ): CustomHighlight[] => {
      if (!field?.value || !field.position) return [];
      const result: CustomHighlight[] = [
        {
          id: `${fieldId}-0`,
          position: field.position,
          label: fieldId,
          pageNumber: field.pageNumber,
          color: VALIDATION_FIELD_BORDER,
          background: VALIDATION_FIELD_BACKGROUND,
        },
      ];
      possibleFields
        .filter((f) => f.value === field.value && f.position && f.pageNumber !== field.pageNumber)
        .forEach((f, i) => {
          result.push({
            id: `${fieldId}-${i + 1}`,
            position: f.position!,
            label: fieldId,
            pageNumber: f.pageNumber,
            color: VALIDATION_FIELD_BORDER,
            background: VALIDATION_FIELD_BACKGROUND,
          });
        });
      return result;
    };

    const onToggleHighlightField = (
      fieldId: string,
      field?: PossibleFieldPositionWithValue | null,
    ) => {
      if (checkIfFieldHighlighted(fieldId)) {
        setHighlightedFields(
          highlightedFields.filter((h) => h.label !== fieldId),
        );
      } else {
        setHighlightedFields([
          ...highlightedFields,
          ...occurrencesOf(fieldId, field),
        ]);
      }
    };

    const syncHighlights = (
      verifierLabelField: PossibleFieldPositionWithValue | null,
      extractionVerifierField: PossibleFieldPositionWithValue | null,
      highlights = highlightedFields,
    ) => {
      const verifierLabelHighlighted = highlights.some(
        (h) => h.label === "VERIFIER_LABEL",
      );
      const extractionVerifierHighlighted = highlights.some(
        (h) => h.label === "EXTRACTION_VERIFIER",
      );

      const updated = highlights.filter(
        (h) => h.label !== "VERIFIER_LABEL" && h.label !== "EXTRACTION_VERIFIER",
      );

      if (verifierLabelHighlighted) {
        updated.push(...occurrencesOf("VERIFIER_LABEL", verifierLabelField));
      }
      if (extractionVerifierHighlighted) {
        updated.push(...occurrencesOf("EXTRACTION_VERIFIER", extractionVerifierField));
      }

      setHighlightedFields(updated);
    };

    useEffect(() => {
      if (!verifierPositions) return;

      if (verifierPositions.verifierLabel) {
        const match = possibleFields.find(
          (f) =>
            f.value === verifierPositions.verifierLabel?.value &&
            f.position &&
            verifierPositions.verifierLabel?.position &&
            f.position.bottomLeftX ===
              verifierPositions.verifierLabel.position.bottomLeftX &&
            f.position.bottomLeftY ===
              verifierPositions.verifierLabel.position.bottomLeftY,
        );
        setVerifierLabel(match ?? null);
      }

      if (verifierPositions.extractionVerifier) {
        const match = possibleFields.find(
          (f) =>
            f.value === verifierPositions.extractionVerifier?.value &&
            f.position &&
            verifierPositions.extractionVerifier?.position &&
            f.position.bottomLeftX ===
              verifierPositions.extractionVerifier.position.bottomLeftX &&
            f.position.bottomLeftY ===
              verifierPositions.extractionVerifier.position.bottomLeftY,
        );
        setExtractionVerifier(match ?? null);
      }
      markDirty(false);
    }, [verifierPositions, possibleFields]);

    const areAllHighlighted = () => {
      const hasVerifierLabel = highlightedFields.some(
        (h) => h.label === "VERIFIER_LABEL",
      );
      const hasExtractionVerifier = highlightedFields.some(
        (h) => h.label === "EXTRACTION_VERIFIER",
      );
      return hasVerifierLabel && hasExtractionVerifier;
    };

    const toggleAll = () => {
      if (areAllHighlighted()) {
        setHighlightedFields(
          highlightedFields.filter(
            (h) =>
              h.label !== "VERIFIER_LABEL" && h.label !== "EXTRACTION_VERIFIER",
          ),
        );
      } else {
        const filtered = highlightedFields.filter(
          (h) =>
            h.label !== "VERIFIER_LABEL" && h.label !== "EXTRACTION_VERIFIER",
        );
        setHighlightedFields([
          ...filtered,
          ...occurrencesOf("VERIFIER_LABEL", verifierLabel),
          ...occurrencesOf("EXTRACTION_VERIFIER", extractionVerifier),
        ]);
      }
    };

    const save = async () => {
      if (!dirtyRef.current) return;

      setError(null);
      await updateVerifierPositions({
        input: {
          verifierLabel: verifierLabel
            ? {
                value: verifierLabel.value!,
                position: stripTypename(verifierLabel.position!),
              }
            : undefined,
          extractionVerifier: extractionVerifier
            ? {
                value: extractionVerifier.value!,
                position: stripTypename(extractionVerifier.position!),
              }
            : undefined,
        },
        templateId,
        clientIdentifier: clientIdentifier!,
      });

      markDirty(false);
    };

    /**
     * Allows the parent to:
     * 1. Trigger a save of this tab's data (`save()`)
     * 2. Check if the tab has unsaved changes (`isDirty()`)
     * 3. Mark the tab as dirty (`markDirty()`)
     */
    useImperativeHandle(ref, () => ({
      save,
      isDirty: () => dirtyRef.current,
      markDirty: () => markDirty(true),
      handleFieldClick: (clickedField: PossibleFieldPositionWithValue) => {
        const id = activeFieldIdRef.current;
        if (!id || !clickedField.position || !clickedField.value) return;
        const resolved: PossibleFieldPositionWithValue = { value: clickedField.value, position: clickedField.position, pageNumber: clickedField.pageNumber };
        setActiveFieldId(null);
        const isVerifierLabel = id === "VERIFIER_LABEL";
        if (isVerifierLabel) {
          setVerifierLabel(resolved);
        } else {
          setExtractionVerifier(resolved);
        }
        markDirty(true);
        const otherId = isVerifierLabel ? "EXTRACTION_VERIFIER" : "VERIFIER_LABEL";
        const otherField = isVerifierLabel ? extractionVerifierRef.current : verifierLabelRef.current;
        const otherHighlighted = highlightedFieldsRef.current.some((h) => h.label === otherId);
        const base = highlightedFieldsRef.current.filter((h) => h.label !== id && h.label !== otherId);
        const updated = [...base, ...occurrencesOf(id, resolved)];
        if (otherHighlighted) updated.push(...occurrencesOf(otherId, otherField));
        setHighlightedFields(updated);
        onNavigateToPage?.(clickedField.pageNumber);
        focusNextInput();
      },
      handleHighlightResized: (label: string, newPosition: Position, pageNumber: number) => {
        getTextAtPosition({
          templateId: templateId!,
          clientIdentifier: clientIdentifier!,
          pageNumber,
          position: newPosition,
        }).then((result) => {
          const fetched = result?.data?.invoiceInterpretation?.getTextAtPosition;
          if (!fetched?.position) return;

          const resolved: PossibleFieldPositionWithValue = {
            value: fetched.value,
            position: fetched.position,
            pageNumber,
          };

          if (label === "VERIFIER_LABEL") {
            setVerifierLabel(resolved);
            markDirty(true);
            syncHighlights(resolved, extractionVerifierRef.current, highlightedFieldsRef.current);
          } else if (label === "EXTRACTION_VERIFIER") {
            setExtractionVerifier(resolved);
            markDirty(true);
            syncHighlights(verifierLabelRef.current, resolved, highlightedFieldsRef.current);
          }
        }).catch((e: any) => {
          setError(e?.message || t("errors.somethingWentWrong"));
        });
      },
    }));

    const onSaveAndContinueClick = async () => {
      navigateAfterSaveRef.current = true;
      try {
        await save();
      } catch (e: any) {
        setError(e.message || t("errors.somethingWentWrong"));
      } finally {
        navigateAfterSaveRef.current = false;
      }
    };

    return (
      <div ref={formRef}>
        {(verifierLabel || extractionVerifier) && (
          <>
            <HorizontalSpacer spacing={1} />
            <Row nogutter justify="end">
              <ToggleAllTextButton
                onClick={toggleAll}
                showHideAll={areAllHighlighted()}
                showAllText={t("templates.showAll")}
                hideAllText={t("templates.hideAll")}
              />
            </Row>
          </>
        )}
        <HorizontalSpacer spacing={1} />
        <FlexRow>
          <FlexText onClick={() => setActiveFieldId("VERIFIER_LABEL")} style={{ cursor: "pointer" }}>
            <Body>{t("templates.selectValidationLabelDescription")}</Body>
          </FlexText>
          <ToggleButton
            onToggle={() =>
              onToggleHighlightField("VERIFIER_LABEL", verifierLabel)
            }
            isOn={checkIfFieldHighlighted("VERIFIER_LABEL")}
            size="medium"
            iconOn={<ViewOnIcon color={theme.colors.white} />}
            iconOff={<ViewOffIcon color={theme.colors.primary} />}
            tabIndex={-1}
          />
        </FlexRow>
        <HorizontalSpacer spacing={0.5} />
        <ActiveFieldWrapper $active={activeFieldId === "VERIFIER_LABEL"} onClick={() => setActiveFieldId("VERIFIER_LABEL")} onFocus={(e) => { setActiveFieldId("VERIFIER_LABEL"); if (e.target instanceof HTMLInputElement) lastFocusedInputRef.current = e.target; }}>
          <AutocompleteSelect
            value={verifierLabel?.value ?? ""}
            onChange={(value) => {
              const posMatch = value.match(/^__pos_(\d+)_(.+)__$/);
              const newField = posMatch
                ? (possibleFields.find((f) => f.position && f.pageNumber === parseInt(posMatch[1]) && positionKey(f.position) === posMatch[2]) ?? null)
                : (possibleFields.find((f) => f.value === value) ?? null);
              setVerifierLabel(newField);
              markDirty(true);
              syncHighlights(newField, extractionVerifier);
              if (newField) {
                if (!checkIfFieldHighlighted("VERIFIER_LABEL")) {
                  onToggleHighlightField("VERIFIER_LABEL", newField);
                }
                onNavigateToPage?.(newField.pageNumber);
              }
              focusNextInput();
            }}
            options={[
              ...buildFieldOptions(possibleFields, totalPages, t("common.page"), t("templates.value")),
              ...(verifierLabel?.value && !possibleFields.some((f) => f.value === verifierLabel?.value)
                ? [{ label: verifierLabel.value, value: verifierLabel.value }]
                : []),
            ]}
          />
        </ActiveFieldWrapper>
        <HorizontalSpacer spacing={0.5} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InfoIconSmall color={theme.colors.textSecondary} />
          <BodySmall color={theme.colors.textSecondary}>
            {t("templates.templateValidationLabelInfo")}
          </BodySmall>
        </div>

        <HorizontalSpacer spacing={2} />
        <Divider />
        <HorizontalSpacer spacing={2} />

        <FlexRow>
          <FlexText onClick={() => setActiveFieldId("EXTRACTION_VERIFIER")} style={{ cursor: "pointer" }}>
            <Body>
              {t("templates.selectValidationMultiplePagesDescription")}
            </Body>
          </FlexText>
          <ToggleButton
            onToggle={() =>
              onToggleHighlightField("EXTRACTION_VERIFIER", extractionVerifier)
            }
            isOn={checkIfFieldHighlighted("EXTRACTION_VERIFIER")}
            size="medium"
            iconOn={<ViewOnIcon color={theme.colors.white} />}
            iconOff={<ViewOffIcon color={theme.colors.primary} />}
            tabIndex={-1}
          />
        </FlexRow>
        <HorizontalSpacer spacing={0.5} />
        <ActiveFieldWrapper $active={activeFieldId === "EXTRACTION_VERIFIER"} onClick={() => setActiveFieldId("EXTRACTION_VERIFIER")} onFocus={(e) => { setActiveFieldId("EXTRACTION_VERIFIER"); if (e.target instanceof HTMLInputElement) lastFocusedInputRef.current = e.target; }}>
          <AutocompleteSelect
            value={extractionVerifier?.value ?? ""}
            onChange={(value) => {
              const posMatch = value.match(/^__pos_(\d+)_(.+)__$/);
              const newField = posMatch
                ? (possibleFields.find((f) => f.position && f.pageNumber === parseInt(posMatch[1]) && positionKey(f.position) === posMatch[2]) ?? null)
                : (possibleFields.find((f) => f.value === value) ?? null);
              setExtractionVerifier(newField);
              markDirty(true);
              syncHighlights(verifierLabel, newField);
              if (newField) {
                if (!checkIfFieldHighlighted("EXTRACTION_VERIFIER")) {
                  onToggleHighlightField("EXTRACTION_VERIFIER", newField);
                }
                onNavigateToPage?.(newField.pageNumber);
              }
              focusNextInput();
            }}
            options={[
              ...buildFieldOptions(possibleFields, totalPages, t("common.page"), t("templates.value")),
              ...(extractionVerifier?.value && !possibleFields.some((f) => f.value === extractionVerifier?.value)
                ? [{ label: extractionVerifier.value, value: extractionVerifier.value }]
                : []),
            ]}
          />
        </ActiveFieldWrapper>
        <HorizontalSpacer spacing={0.5} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <InfoIconSmall color={theme.colors.textSecondary} />
          <BodySmall color={theme.colors.textSecondary}>
            {t("templates.templateValidationMultiplePagesInfo")}
          </BodySmall>
        </div>

        <HorizontalSpacer spacing={2} />
        <Divider />
        <HorizontalSpacer spacing={2} />
        {error && <Alert type="error" title={error} />}
        <Row nogutter justify="end">
          <OutlinedButton
            title={t("random.saveAndContinue")}
            iconRight={<ChevronSmallRightIcon color={theme.colors.primary} />}
            color={theme.colors.primary}
            medium
            onClick={onSaveAndContinueClick}
            disabled={loading || !dirty}
          />
        </Row>
      </div>
    );
  },
);

export default EditValidationFields;
